import React, { useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSettingsStore } from '../stores/settingsStore'
import { useMarketStore } from '../services/store'
import { getFirebaseAuth, getOrWaitForAuthToken, getCachedIdToken } from '../services/firebase'
import { applyChainDiff } from '../lib/diff-engine'
import type { MarketAnalysisResponse } from '@options-oracle/types'
import { writeLiveTick } from '../stores/liveTickAtoms'
import { toast } from 'sonner'
import { useAlertStore } from '../hooks/useAlertStore'
import { isMarketHours, getCanonicalSymbol } from '../config/constants'

let globalLastStoreWrite = 0

// ----------------------------------------------------------------------------
// In-Memory Token Cache (10-minute sliding TTL)
// ----------------------------------------------------------------------------
async function getCachedAuthToken(firebaseUser?: any): Promise<string> {
    const token = await getOrWaitForAuthToken()
    return token || ''
}

/**
 * GlobalMarketEngine
 * 
 * Bulletproof Multi-Layer Live Market Data Engine
 * Mounted ONCE in the application root (_auth.tsx).
 * 
 * Layers:
 * 1. Primary: Persistent Server-Sent Events (SSE) Push Stream (/api/live-stream)
 * 2. Failover: Single-Flight HTTP Polling (/api/live-feed) if SSE is blocked
 * 3. 10-Second Watchdog Heartbeat: Autonomous dead-connection recovery
 * 4. Tab Awake & Focus Instant Resumption
 * 
 * GUARANTEE: Live data NEVER stops under any network condition or tab sleep.
 */
export function GlobalMarketEngine() {
    const queryClient = useQueryClient()

    // 1. Settings Selectors
    const activeSymbol = useSettingsStore(s => s.symbol) || ''
    const settingsExpiry = useSettingsStore(s => s.expiry)
    const activeMode = useSettingsStore(s => s.mode)
    const settingsRange = useSettingsStore(s => s.range)
    const settingsBaseTime = useSettingsStore(s => s.baseTime)
    const settingsIsLive = useSettingsStore(s => s.isLive)
    const settingsHistoricalDate = useSettingsStore(s => s.historicalDate)
    const inspectionSource = useSettingsStore(s => s.inspectionSource)

    const symbolExpiries = useMarketStore((state) => state.expiryMap[activeSymbol])
    const effectiveExpiry: string = settingsExpiry || (symbolExpiries && symbolExpiries.length > 0 ? (symbolExpiries[0] as string) : '')
    
    const { isAuthenticated, loading, firebaseUser } = useAuth()

    const settingsRef = useRef({
        activeSymbol,
        effectiveExpiry,
        activeMode,
        settingsRange,
        settingsBaseTime,
        inspectionSource,
        isAuthenticated,
        loading,
        firebaseUser,
    })
    settingsRef.current = {
        activeSymbol,
        effectiveExpiry,
        activeMode,
        settingsRange,
        settingsBaseTime,
        inspectionSource,
        isAuthenticated,
        loading,
        firebaseUser,
    }

    // ========================================================================
    // EXPIRY FETCHING (With Instant Direct Google Sheets CSV Fallback)
    // ========================================================================
    const { data: fetchedExpiries } = useQuery({
        queryKey: ['expiries', activeSymbol, settingsIsLive ? 'live' : settingsHistoricalDate],
        queryFn: async () => {
            if (!activeSymbol) return []

            // 1. Try Backend API first
            try {
                const token = await getOrWaitForAuthToken()
                let url = `/api/expiries?symbol=${encodeURIComponent(activeSymbol)}`
                if (!settingsIsLive && settingsHistoricalDate) {
                    url += `&date=${encodeURIComponent(settingsHistoricalDate)}`
                }
                const resp = await fetch(url, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                })
                if (resp.ok) {
                    const contentType = resp.headers.get('content-type') || ''
                    if (contentType.includes('application/json')) {
                        const data = await resp.json()
                        if (Array.isArray(data) && data.length > 0) {
                            return data as string[]
                        }
                    }
                }
            } catch {}


            // 2. Direct Browser Fallback: Google Sheets Expiry CSV (Always CORS enabled)
            try {
                const csvUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQpDYt-WXR1FfLdVbWUmgjbgsFJYlJZvTiXBvBmXqe0JZuQ65f2hLiUtrmN4MLm5xycG9aC5oQ2kHFO/pub?gid=0&single=true&output=csv'
                const resp = await fetch(csvUrl)
                if (resp.ok) {
                    const text = await resp.text()
                    const lines = text.split(/\r?\n/)
                    const validDates: string[] = []

                    const canonSym = (getCanonicalSymbol(activeSymbol) || activeSymbol) as string

                    const now = new Date()
                    const istDate = new Date(now.getTime() + (5.5 * 60 + now.getTimezoneOffset()) * 60000)
                    const todayIso = istDate.toISOString().split('T')[0] || ''

                    for (let i = 1; i < lines.length; i++) {
                        const line = lines[i]?.replace(/\r/g, '').trim()
                        if (!line) continue
                        const [rawSym, rawDate] = line.split(',')
                        if (rawSym && rawDate) {
                            const sym = rawSym.trim().toUpperCase()
                            const rowSym = (getCanonicalSymbol(sym) || sym) as string
                            if (rowSym === canonSym) {
                                const cleanDate = rawDate.replace(/[^0-9-]/g, '').trim()
                                const parts = cleanDate.split('-')
                                if (parts.length === 3) {
                                    const day = parseInt(parts[0] || '0', 10)
                                    const month = parseInt(parts[1] || '0', 10)
                                    const year = parseInt(parts[2] || '0', 10)
                                    if (day > 0 && month > 0 && year >= 2024) {
                                        const expIso = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
                                        if (expIso >= todayIso && !validDates.includes(cleanDate)) {
                                            validDates.push(cleanDate)
                                        }
                                    }
                                }
                            }
                        }
                    }
                    if (validDates.length > 0) {
                        return validDates.slice(0, 2)
                    }
                }
            } catch (err) {
                console.warn('[GlobalMarketEngine] Direct CSV expiry fetch failed:', err)
            }

            return []
        },
        enabled: !!activeSymbol && inspectionSource !== 'HistoricalSnapshot',
        staleTime: 5 * 60 * 1000,
    })

    useEffect(() => {
        if (inspectionSource === 'HistoricalSnapshot') return
        if (fetchedExpiries && Array.isArray(fetchedExpiries) && fetchedExpiries.length > 0) {
            const store = useMarketStore.getState()
            const currentExpiries = store.expiryMap[activeSymbol] || []
            if (JSON.stringify(currentExpiries) !== JSON.stringify(fetchedExpiries)) {
                store.setExpiryForSymbol(activeSymbol, fetchedExpiries as string[])
            }

            const currentSelectedExpiry = useSettingsStore.getState().expiry
            if (!currentSelectedExpiry || !fetchedExpiries.includes(currentSelectedExpiry)) {
                const firstLiveExpiry = fetchedExpiries[0]
                if (firstLiveExpiry) {
                    setTimeout(() => useSettingsStore.getState().setExpiry(firstLiveExpiry), 0)
                }
            }
        }
    }, [fetchedExpiries, activeSymbol, inspectionSource])

    // ========================================================================
    // HYDRATE ZUSTAND STORE & ATOMS
    // ========================================================================
    const prevChainRef = useRef<{ options: any[]; rows: any[] } | null>(null)
    const prevMetaRef = useRef<any>(null)
    const lastSuccessfulTickRef = useRef<number>(Date.now())

    const handleNewLiveTick = useCallback((workerAnalysis: MarketAnalysisResponse, sym: string, exp: string, latencyMs?: number, dataSource?: string, lastSyncTime?: string) => {
        if (!workerAnalysis || !sym) return

        const waSpot = workerAnalysis.spotPrice || (workerAnalysis as any).ltp
        if (!waSpot || waSpot <= 0) return

        lastSuccessfulTickRef.current = Date.now()

        const now = Date.now()
        const timeSinceLastWrite = now - globalLastStoreWrite
        if (timeSinceLastWrite < 40) return
        globalLastStoreWrite = now

        const waVix = (workerAnalysis as any).vix || 0
        const waDayChange = (workerAnalysis as any).dayChange || (workerAnalysis as any).ltpChange || 0
        const waDayChangeP = (workerAnalysis as any).dayChangeP || (workerAnalysis as any).ltpChangePercent || 0
        const waVixChange = (workerAnalysis as any).vixChange || 0
        const waVixChangeP = (workerAnalysis as any).vixChangeP || 0
        const waVixChangePercent = (workerAnalysis as any).vixChangePercent || (workerAnalysis as any).vixChangeP || 0
        const waMetrics = workerAnalysis.analysis || (workerAnalysis as any)

        const meta = {
            dayChange: waDayChange,
            dayChangeP: waDayChangeP,
            vix: waVix,
            vixChange: waVixChange,
            vixChangeP: waVixChangeP,
            vixChangePercent: waVixChangePercent,
            latencyMs: typeof latencyMs === 'number' ? latencyMs : 0,
            dataSource: dataSource || 'EDGE_API',
            lastSyncTime: lastSyncTime || (workerAnalysis as any).lastSyncTime || '',
        }

        const prevMeta = prevMetaRef.current
        const metaUnchanged = prevMeta &&
            prevMeta.dayChange === meta.dayChange &&
            prevMeta.dayChangeP === meta.dayChangeP &&
            prevMeta.vix === meta.vix &&
            prevMeta.vixChange === meta.vixChange

        const waOptions = workerAnalysis.options || (workerAnalysis as any).optionChain || []

        const rawMappedChain = waOptions
            .map((opt: any) => ({
                strike: opt.strike,
                ce: {
                    ltp: opt.ce?.ltp ?? opt.callPrice ?? 0,
                    oi: opt.ce?.oi ?? opt.callOpenInterest ?? 0,
                    vol: opt.ce?.vol ?? opt.callVolume ?? 0,
                    oiChange: opt.ce?.oiChange ?? opt.callOpenInterestChange ?? 0,
                    change: opt.ce?.change ?? opt.callChange ?? 0,
                    changePercent: opt.ce?.changePercent ?? opt.callChangePercent ?? 0,
                    iv: opt.ce?.iv ?? opt.callImpliedVolatility ?? 0,
                    delta: opt.ce?.delta ?? opt.callDelta ?? 0,
                    gamma: opt.ce?.gamma ?? opt.callGamma ?? 0,
                    theta: opt.ce?.theta ?? opt.callTheta ?? 0,
                    vega: opt.ce?.vega ?? opt.callVega ?? 0,
                    instrumentKey: opt.ce?.instrumentKey || opt.ceInstrumentKey || opt.callInstrumentKey || opt.instrumentKey,
                },
                pe: {
                    ltp: opt.pe?.ltp ?? opt.putPrice ?? 0,
                    oi: opt.pe?.oi ?? opt.putOpenInterest ?? 0,
                    vol: opt.pe?.vol ?? opt.putVolume ?? 0,
                    oiChange: opt.pe?.oiChange ?? opt.putOpenInterestChange ?? 0,
                    change: opt.pe?.change ?? opt.putChange ?? 0,
                    changePercent: opt.pe?.changePercent ?? opt.putChangePercent ?? 0,
                    iv: opt.pe?.iv ?? opt.putImpliedVolatility ?? 0,
                    delta: opt.pe?.delta ?? opt.putDelta ?? 0,
                    gamma: opt.pe?.gamma ?? opt.putGamma ?? 0,
                    theta: opt.pe?.theta ?? opt.putTheta ?? 0,
                    vega: opt.pe?.vega ?? opt.putVega ?? 0,
                    instrumentKey: opt.pe?.instrumentKey || opt.peInstrumentKey || opt.putInstrumentKey || opt.instrumentKey,
                },
            }))
            .sort((a: any, b: any) => a.strike - b.strike)

        const prevChain = prevChainRef.current
        const chainRows = applyChainDiff(prevChain?.rows, rawMappedChain)

        if (chainRows !== prevChain?.rows) {
            prevChainRef.current = { options: waOptions, rows: chainRows }
        }

        const store = useMarketStore.getState()
        const prevLtp = store.indexLtp[sym]

        // Dual-write to Jotai atoms for surgical components
        const fullMarketData = {
            symbol: sym,
            spotPrice: waSpot,
            dayChange: meta.dayChange,
            dayChangeP: meta.dayChangeP,
            vix: meta.vix,
            vixChange: meta.vixChange,
            vixChangeP: meta.vixChangeP,
            options: waOptions,
            timestamp: Date.now(),
            lastSyncTime: meta.lastSyncTime,
            expiry: exp,
            isLive: true,
            atmStrike: waMetrics?.atmStrike || 0,
        } as any

        writeLiveTick(sym, waSpot, meta, waMetrics, fullMarketData)

        const hasCurrentData = !!store.data[sym] && store.data[sym]!.length > 0 && (store.indexLtp[sym] || 0) > 0
        if (hasCurrentData && prevChain?.rows === chainRows && prevLtp === waSpot && metaUnchanged) {
            store.updateTimestamp()
            return
        }
        prevMetaRef.current = meta

        // Single atomic state update to Zustand: updates data, fullData, indexLtp, meta, and metrics in one pass
        store.updateTick(sym, waSpot, chainRows, meta, waMetrics, fullMarketData)

        // Evaluate Custom Alerts
        try {
            const { alerts, updateLastTriggered } = useAlertStore.getState()
            const activeAlerts = alerts.filter((a: any) => a.isActive && a.symbol === sym)
            
            if (activeAlerts.length > 0) {
                const alertNow = Date.now()
                for (const alert of activeAlerts) {
                    if (alert.lastTriggered && alertNow - alert.lastTriggered < 60000) continue

                    const row = chainRows.find(r => r.strike === alert.strike)
                    if (!row) continue

                    const data = alert.type === 'CE' ? row.ce : row.pe
                    if (!data) continue

                    let currentValue = 0
                    if (alert.metric === 'ltp') currentValue = data.ltp
                    else if (alert.metric === 'oi') currentValue = data.oi
                    else if (alert.metric === 'oiChg') currentValue = data.oiChange
                    else if (alert.metric === 'volume') currentValue = data.vol
                    else if (alert.metric === 'volOiDiff') currentValue = data.vol - data.oi
                    else if (alert.metric === 'volOiChg') currentValue = data.vol - data.oiChange

                    let isTriggered = false
                    if (alert.operator === 'gt' && currentValue > alert.threshold) isTriggered = true
                    if (alert.operator === 'lt' && currentValue < alert.threshold) isTriggered = true

                    if (isTriggered) {
                        toast.success(`Alert Triggered: ${alert.symbol} ${alert.strike} ${alert.type}`, {
                            description: `${alert.metric.toUpperCase()} is ${alert.operator === 'gt' ? '>' : '<'} ${alert.threshold.toLocaleString('en-IN')} (Current: ${currentValue.toLocaleString('en-IN')})`,
                            duration: 8000,
                        })
                        updateLastTriggered(alert.id, alertNow)
                    }
                }
            }
        } catch (err) {
            console.error('[GlobalMarketEngine] Error evaluating alerts:', err)
        }
    }, [])

    // ========================================================================
    // BULLETPROOF LIVE FEED ENGINE WITH DEDICATED OFF-THREAD WEB WORKER
    // ========================================================================
    const workerRef = useRef<Worker | null>(null)
    const watchdogTimerRef = useRef<any>(null)
    const lastPollNowTriggerRef = useRef<number>(0)

    // Centralized Helper to dispatch START configuration to worker
    const sendStartToWorker = useCallback((targetWorker?: Worker | null) => {
        const w = targetWorker || workerRef.current
        if (!w) return

        const current = settingsRef.current
        if (current.inspectionSource === 'HistoricalSnapshot' || !current.activeSymbol || !current.effectiveExpiry) {
            console.log('[GlobalMarketEngine] Skipping worker start (historical mode or missing symbol/expiry)')
            return
        }

        console.log(`[GlobalMarketEngine] Dispatching START to worker: symbol=${current.activeSymbol}, expiry=${current.effectiveExpiry}, mode=${current.activeMode || 'OI'}`)

        getOrWaitForAuthToken().then((token) => {
            if (workerRef.current && workerRef.current !== w) return
            try {
                w.postMessage({
                    type: 'START',
                    config: {
                        symbol: current.activeSymbol,
                        expiry: current.effectiveExpiry,
                        mode: current.activeMode || 'OI',
                        range: current.settingsRange || 12,
                        baseTime: current.settingsBaseTime,
                        token: token || undefined,
                        intervalMs: 2000,
                    },
                })
            } catch (err) {
                console.warn('[GlobalMarketEngine] Failed to post START to worker:', err)
            }
        })
    }, [])

    // 1. Worker Lifecycle (Initialize with auto-recovery capability)
    const initWorker = useCallback(() => {
        if (workerRef.current) {
            try {
                workerRef.current.postMessage({ type: 'STOP' })
                workerRef.current.terminate()
            } catch {}
            workerRef.current = null
        }

        lastSuccessfulTickRef.current = Date.now()
        lastPollNowTriggerRef.current = Date.now()

        try {
            const worker = new Worker(new URL('../workers/live-feed.worker.ts', import.meta.url), {
                type: 'module',
            })
            workerRef.current = worker

            worker.onmessage = (event: MessageEvent) => {
                const msg = event.data
                if (!msg || typeof msg !== 'object') return

                if (msg.type === 'TICK' && msg.payload) {
                    const src = msg.dataSource || 'EDGE_API'
                    const srcLabel = src === 'EDGE_API' 
                        ? '🟢 [Tier 1: Cloudflare Edge API]' 
                        : src === 'UPSTOX_PROXY' 
                        ? '🔵 [Tier 2: Dedicated Server Proxy]' 
                        : '🟡 [Tier 3: Google Sheets Live CSV]'

                    console.log(
                        `%c[Live Feed] ${srcLabel} Symbol: ${msg.symbol} | Expiry: ${msg.expiry} | Spot: ${msg.payload?.spotPrice} | ATM: ${msg.payload?.atmStrike} | Latency: ${msg.latencyMs}ms | Last Sync: ${msg.lastSyncTime || 'N/A'}`,
                        'color: #10b981; font-weight: bold; font-size: 11px;'
                    )

                    handleNewLiveTick(msg.payload, msg.symbol, msg.expiry, msg.latencyMs, msg.dataSource, msg.lastSyncTime)
                } else if (msg.type === 'STATUS') {
                    console.log(`[GlobalMarketEngine] Live Worker Status:`, msg.status, msg.error || '')
                }
            }

            worker.onerror = (err) => {
                console.error('[GlobalMarketEngine] Live worker error event:', err)
            }

            // Immediately send current config to the newly created worker!
            sendStartToWorker(worker)

            return worker
        } catch (e) {
            console.error('[GlobalMarketEngine] Failed to initialize live-feed.worker:', e)
            return null
        }
    }, [handleNewLiveTick, sendStartToWorker])

    useEffect(() => {
        initWorker()

        // 2. Autonomous Watchdog Heartbeat & Self-Healing
        watchdogTimerRef.current = setInterval(() => {
            const current = settingsRef.current
            if (current.inspectionSource === 'HistoricalSnapshot' || !current.activeSymbol || !current.effectiveExpiry) return

            // If tab is in background (hidden), don't restart worker (paused intentionally)
            if (document.visibilityState !== 'visible') return

            const now = Date.now()
            const timeSinceLastTick = now - lastSuccessfulTickRef.current
            const pollThreshold = 8000
            const restartThreshold = 25000

            if (timeSinceLastTick > restartThreshold) {
                console.warn(`[GlobalMarketEngine] Watchdog: worker silent for ${timeSinceLastTick}ms. Auto-restarting worker...`)
                initWorker()
                lastSuccessfulTickRef.current = Date.now()
            } else if (timeSinceLastTick > pollThreshold) {
                if (now - lastPollNowTriggerRef.current > 4000) {
                    lastPollNowTriggerRef.current = now
                    console.log(`[GlobalMarketEngine] Watchdog triggered POLL_NOW (silent for ${timeSinceLastTick}ms)`)
                    workerRef.current?.postMessage({ type: 'POLL_NOW' })
                }
            }
        }, 3000)

        return () => {
            if (watchdogTimerRef.current) clearInterval(watchdogTimerRef.current)
            if (workerRef.current) {
                try {
                    workerRef.current.postMessage({ type: 'STOP' })
                    workerRef.current.terminate()
                } catch {}
                workerRef.current = null
            }
        }
    }, [initWorker])

    // 3. Configuration Sync to Worker (Instant <50ms Symbol / Expiry Change, 2-Second Cadence)
    useEffect(() => {
        if (!workerRef.current) {
            initWorker()
            return
        }

        console.log(`[GlobalMarketEngine] Config change detected: symbol=${activeSymbol}, effectiveExpiry=${effectiveExpiry}, inspectionSource=${inspectionSource}`)

        if (inspectionSource === 'HistoricalSnapshot' || !activeSymbol || !effectiveExpiry) {
            console.log('[GlobalMarketEngine] Pausing live worker (Historical mode or missing symbol/expiry)')
            workerRef.current.postMessage({ type: 'STOP' })
            return
        }

        lastSuccessfulTickRef.current = Date.now()
        lastPollNowTriggerRef.current = Date.now()

        sendStartToWorker(workerRef.current)
    }, [activeSymbol, effectiveExpiry, activeMode, settingsRange, settingsBaseTime, inspectionSource, initWorker, sendStartToWorker])

    const lastWakeRef = useRef<number>(0)

    // 4. Page Visibility & Tab Awake Handler (Instant Resumption upon returning to tab)
    useEffect(() => {
        const handleWake = () => {
            if (document.visibilityState === 'visible') {
                const now = Date.now()
                if (now - lastWakeRef.current < 1000) return // 1s throttle prevents duplicate triggers
                lastWakeRef.current = now

                const timeSinceLastTick = now - lastSuccessfulTickRef.current
                console.log(`[GlobalMarketEngine] Tab active/visible (silent for ${timeSinceLastTick}ms). Resuming live feed...`)
                
                lastSuccessfulTickRef.current = now
                lastPollNowTriggerRef.current = now

                // If tab was away for long (>30s) or worker is missing, cleanly re-init worker
                if (!workerRef.current || (timeSinceLastTick > 30000 && settingsRef.current.activeSymbol && settingsRef.current.effectiveExpiry)) {
                    console.log('[GlobalMarketEngine] Tab wake: Re-initializing worker with fresh session')
                    initWorker()
                } else {
                    sendStartToWorker(workerRef.current)
                    workerRef.current?.postMessage({ type: 'RESUME' })
                    workerRef.current?.postMessage({ type: 'POLL_NOW' })
                }
            } else {
                // Pause polling when tab is hidden to conserve resources
                workerRef.current?.postMessage({ type: 'PAUSE' })
            }
        }

        document.addEventListener('visibilitychange', handleWake)
        window.addEventListener('focus', handleWake)
        window.addEventListener('pageshow', handleWake)
        window.addEventListener('online', handleWake)
        document.addEventListener('resume' as any, handleWake)

        // Run once on mount to ensure correct initial state
        if (document.visibilityState === 'hidden') {
            workerRef.current?.postMessage({ type: 'PAUSE' })
        }

        return () => {
            document.removeEventListener('visibilitychange', handleWake)
            window.removeEventListener('focus', handleWake)
            window.removeEventListener('pageshow', handleWake)
            window.removeEventListener('online', handleWake)
            document.removeEventListener('resume' as any, handleWake)
        }
    }, [initWorker, sendStartToWorker])

    // 5. Realtime Auth Token Synchronization with Worker
    useEffect(() => {
        const handleTokenUpdate = (e: any) => {
            const token = e.detail?.token
            if (token && workerRef.current) {
                workerRef.current.postMessage({ type: 'UPDATE_TOKEN', token })
            }
        }
        window.addEventListener('auth_token_changed', handleTokenUpdate)
        return () => window.removeEventListener('auth_token_changed', handleTokenUpdate)
    }, [])

    return null
}
